import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Button, notification, Popconfirm, Table } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks/effects'
import { DELETE_SCRAPING_TAG } from '../../graphql/mutations'
import { SCRAPING_TAGS } from '../../graphql/queries'
import { CUSTOM, SCRAPING_TAG_TYPES } from '../../constants'
import { ScrapingTagEditorModal } from './sections'

const ScrapingTagsManagementPage = () => {
  useDocumentTitle('Scraping tags management')

  const [scrapingTagEditorModalSettings, setScrapingTagEditorModalSettings] =
    useState({
      tag: null,
      isModalOpen: false,
    })

  const {
    data: { scrapingTags = [] } = {},
    loading: isLoadingTags,
    refetch: refetchScrapingTags,
  } = useQuery(SCRAPING_TAGS, {
    fetchPolicy: 'no-cache',
  })

  const [deleteScrapingTag, { loading: isDeletingScrapingTag }] = useMutation(
    DELETE_SCRAPING_TAG,
    {
      onCompleted() {
        refetchScrapingTags()
        notification.open({
          message: 'Tag deleted.',
          type: 'success',
        })
      },
      onError({ message }) {
        notification.open({
          message: 'Failed to delete tag.',
          description: message,
          type: 'error',
          duration: 0,
        })
      },
    },
  )

  const tableColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: type => SCRAPING_TAG_TYPES[type].name,
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: () => (
        <Button
          type="primary"
          onClick={() =>
            setScrapingTagEditorModalSettings({
              tag: null,
              isModalOpen: true,
            })
          }
        >
          Create tag
        </Button>
      ),
      align: 'right',
      render: tag => (
        <>
          <Button
            style={{ marginRight: '15px' }}
            onClick={() =>
              setScrapingTagEditorModalSettings({
                tag,
                isModalOpen: true,
              })
            }
            disabled={tag.type !== CUSTOM}
          >
            Edit
          </Button>
          <Popconfirm
            title="Deleting tag will also remove it from all test cases that have it. Do you want to continue?"
            onConfirm={() => deleteScrapingTag({ variables: { id: tag.id } })}
            placement="left"
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} disabled={tag.type !== CUSTOM} />
          </Popconfirm>
        </>
      ),
    },
  ]

  return (
    <>
      <Table
        loading={isLoadingTags || isDeletingScrapingTag}
        rowKey="id"
        columns={tableColumns}
        dataSource={scrapingTags}
        size="small"
        pagination={{
          pageSize: DEFAULT_PAGE_SIZE,
          showSizeChanger: false,
        }}
      />

      <ScrapingTagEditorModal
        {...scrapingTagEditorModalSettings}
        refetchScrapingTags={refetchScrapingTags}
        closeModal={() =>
          setScrapingTagEditorModalSettings({
            tag: undefined,
            isModalOpen: false,
          })
        }
      />
    </>
  )
}

export default ScrapingTagsManagementPage
